import { Link, useNavigate } from "react-router-dom";
import { Music2, CalendarDays, Search, Inbox, Mail, ArrowRight, Sparkles, MapPin, Bell, Shield, ShieldCheck, Users2, FlaskConical, Headphones } from "lucide-react";

const FEAT_MUSICIAN = "https://images.unsplash.com/photo-1595775948902-f04682c9e588?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwyfHxvcmNoZXN0cmElMjByZWhlYXJzYWx8ZW58MHx8fHwxNzc4MTU5NzU5fDA&ixlib=rb-4.1.0&q=85";
const FEAT_BOOKER = "https://images.unsplash.com/photo-1519683384663-c9b34271669a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwxfHxvcmNoZXN0cmElMjByZWhlYXJzYWx8ZW58MHx8fHwxNzc4MTU5NzU5fDA&ixlib=rb-4.1.0&q=85";

// Product-first hero: a single large diary screenshot with a floating
// "next event" caption card that anchors the visual to the app's real data.
function HeroVisual() {
  return (
    <div className="relative" data-testid="hero-visual">
      <div className="rounded-2xl border border-ivory-200 shadow-card overflow-hidden bg-white">
        <img
          src="/press/desktop-diary.jpg"
          alt="AMDS diary — monthly view with rehearsals, concerts and teaching"
          className="w-full h-auto block"
        />
      </div>
      <div className="absolute -bottom-5 -left-5 sm:-left-8 card-base p-4 w-56 hidden sm:block">
        <div className="text-[11px] uppercase tracking-widest text-ink-500 mb-1">Sat 18 Jul</div>
        <div className="font-heading font-semibold text-ink-900">Meridian Chamber Players</div>
        <div className="text-sm text-ink-600">Schubert 5 · St Martin-in-the-Fields</div>
        <div className="mt-2 inline-flex items-center gap-1.5 pill bg-sage-100 text-sage-600">
          <span className="w-1.5 h-1.5 rounded-full bg-sage-500" /> unavailable
        </div>
      </div>
    </div>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-ivory text-ink-900">
      {/* Top */}
      <header className="glass-bar sticky top-0 z-40 border-b">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-9 h-9 rounded-xl bg-sage-500 text-white grid place-items-center">
              <Music2 size={20} strokeWidth={2.2} />
            </span>
            <span className="font-heading font-semibold tracking-tight text-lg">AMDS</span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/pricing" className="btn-ghost text-sm hidden sm:inline-flex" data-testid="land-pricing-link">Pricing</Link>
            <Link to="/login" className="btn-ghost text-sm" data-testid="land-login-link">Log in</Link>
            <Link to="/register" className="btn-primary text-sm" data-testid="land-signup-btn">
              Get started <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-12 sm:pt-20 pb-12">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="label-overline mb-4">
              <span className="inline-flex items-center gap-2"><Sparkles size={14} /> Diary, not drama</span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
              An honest little diary <span className="text-sage-600">for all musicians</span> and the people who book them.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-ink-600 max-w-xl leading-relaxed">
              Mark your dates, get found by conductors and fixers, accept or decline a gig in two taps — and the booking lands straight in your calendar. No WhatsApp threads, no public ratings, no nonsense.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <button onClick={() => navigate("/register?role=musician")} className="btn-primary" data-testid="land-cta-musician">
                I&apos;m a musician <ArrowRight size={16} />
              </button>
              <button onClick={() => navigate("/register?role=booker")} className="btn-secondary" data-testid="land-cta-booker">
                I book players
              </button>
              <button onClick={() => navigate("/register?role=conductor")} className="btn-secondary" data-testid="land-cta-conductor">
                I conduct
              </button>
            </div>
            <div className="mt-8 flex items-center gap-6 text-sm text-ink-500">
              <span className="inline-flex items-center gap-2"><CalendarDays size={16} className="text-sage-500" /> Apple · Google · Outlook</span>
              <span className="inline-flex items-center gap-2"><Bell size={16} className="text-sage-500" /> Instant alerts</span>
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <HeroVisual />
          </div>
        </div>
      </section>

      {/* Real product screenshots — three panels, each captioned. Ships with
          the marketing demo dataset so screenshots always look populated. */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-6 pb-2" data-testid="showcase-section">
        <div className="space-y-8 sm:space-y-12">
          {[
            {
              testid: "showcase-diary",
              icon: CalendarDays,
              eyebrow: "Your Diary",
              caption: "Your diary stays private. Only your availability is shared.",
              img: "/press/desktop-diary.jpg",
              alt: "AMDS diary — a musician's monthly calendar with rehearsals, concerts, teaching blocks and personal practice colour-coded.",
            },
            {
              testid: "showcase-deps",
              icon: Headphones,
              eyebrow: "Dep Requests",
              caption: "Find available musicians when you need them.",
              img: "/press/desktop-deps.jpg",
              alt: "AMDS dep board — a list of open dep requests filtered to the musician's own instrument, with venue, fee and date.",
            },
            {
              testid: "showcase-availability",
              icon: MapPin,
              eyebrow: "Availability",
              caption: "See who is free before you start phoning around.",
              img: "/press/desktop-search.jpg",
              alt: "AMDS musician search — availability at a glance across a chosen date, showing available, tentative and unavailable players by instrument and location.",
            },
          ].map((row, idx) => (
            <div
              key={row.testid}
              className={`grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-10 items-center ${
                idx % 2 === 1 ? "md:[&>:first-child]:order-2" : ""
              }`}
              data-testid={row.testid}
            >
              <div className="rounded-2xl border border-ivory-200 shadow-sm overflow-hidden bg-white">
                <img
                  src={row.img}
                  alt={row.alt}
                  className="w-full h-auto block"
                  loading="lazy"
                />
              </div>
              <div>
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-sage-100 text-sage-700 mb-3" aria-hidden>
                  <row.icon size={20} />
                </div>
                <div className="label-overline">{row.eyebrow}</div>
                <p className="font-heading text-2xl sm:text-3xl font-semibold text-ink-900 mt-2 leading-tight">
                  {row.caption}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Trust banner + values row — sits between hero and two-track features */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-2 pb-4" data-testid="trust-banner-section">
        <Link
          to="/privacy-security"
          className="block group rounded-2xl border border-sage-200 bg-sage-100/60 hover:bg-sage-100 transition p-5 sm:p-6"
          data-testid="trust-banner"
        >
          <div className="flex items-start gap-4">
            <div className="shrink-0 w-11 h-11 rounded-full bg-white border border-sage-200 flex items-center justify-center text-sage-600">
              <Shield size={20} />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-heading text-lg sm:text-xl font-semibold text-ink-900">Your privacy comes first</h3>
              <p className="text-ink-700 text-sm sm:text-base leading-relaxed mt-1">
                Your diary is completely private. Other musicians only ever see your
                availability — never your diary entries, event titles, venues or personal notes.
              </p>
              <span className="text-sage-600 text-sm mt-2 inline-flex items-center gap-1 group-hover:underline">
                Learn more about our Privacy &amp; Security commitment <ArrowRight size={14} />
              </span>
            </div>
          </div>
        </Link>

        {/* Three quiet trust indicators */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mt-4" data-testid="trust-indicators">
          <div className="rounded-xl border border-ivory-200 bg-white p-4 flex gap-3 items-start" data-testid="trust-indicator-privacy">
            <ShieldCheck size={20} className="text-sage-600 shrink-0 mt-0.5" />
            <div>
              <div className="font-heading font-semibold text-ink-900 text-sm">Privacy First</div>
              <p className="text-ink-500 text-xs mt-0.5 leading-relaxed">Your diary stays private.</p>
            </div>
          </div>
          <div className="rounded-xl border border-ivory-200 bg-white p-4 flex gap-3 items-start" data-testid="trust-indicator-musicians">
            <Users2 size={20} className="text-sage-600 shrink-0 mt-0.5" />
            <div>
              <div className="font-heading font-semibold text-ink-900 text-sm">Built by Musicians</div>
              <p className="text-ink-500 text-xs mt-0.5 leading-relaxed">Designed from real-world ensemble experience.</p>
            </div>
          </div>
          <div className="rounded-xl border border-ivory-200 bg-white p-4 flex gap-3 items-start" data-testid="trust-indicator-beta">
            <FlaskConical size={20} className="text-sage-600 shrink-0 mt-0.5" />
            <div>
              <div className="font-heading font-semibold text-ink-900 text-sm">Community Beta</div>
              <p className="text-ink-500 text-xs mt-0.5 leading-relaxed">Improved through feedback from musicians.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Two-track features */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
        <div className="grid md:grid-cols-2 gap-6">
          <article className="card-base overflow-hidden">
            <div className="aspect-[16/10] overflow-hidden border-b border-ivory-200">
              <img src={FEAT_MUSICIAN} alt="Violinist practising" className="w-full h-full object-cover" />
            </div>
            <div className="p-6">
              <div className="label-overline mb-2">For musicians</div>
              <h2 className="font-heading text-2xl font-semibold mb-2">Keep your diary in one place.</h2>
              <p className="text-ink-600 leading-relaxed">Mark availability in seconds. Repeat weekly patterns. Receive booking requests with full details and reply with one tap. <span className="text-ink-500">Dep finder is scoped to your own instrument — perfect for filling your chair when you can&apos;t make a gig.</span></p>
            </div>
          </article>
          <article className="card-base overflow-hidden">
            <div className="aspect-[16/10] overflow-hidden border-b border-ivory-200">
              <img src={FEAT_BOOKER} alt="Conductor reading score" className="w-full h-full object-cover" />
            </div>
            <div className="p-6">
              <div className="label-overline mb-2">For bookers &amp; conductors</div>
              <h2 className="font-heading text-2xl font-semibold mb-2">Find an available player — fast.</h2>
              <p className="text-ink-600 leading-relaxed">Search across every instrument by date and travel radius. Create events and send booking requests directly. <span className="text-ink-500">Full ensemble-infrastructure tools (sectional broadcasts, payroll, multi-org calendars) arrive post-beta.</span></p>
            </div>
          </article>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
        <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mb-8">How it works</h2>
        <div className="grid sm:grid-cols-3 gap-4">
          {[
            { icon: CalendarDays, title: "Mark your dates", body: "Tap a day. Set available, evening only, or unavailable." },
            { icon: Search, title: "Get found", body: "Bookers search by instrument, date and distance — and see your real availability." },
            { icon: Inbox, title: "Accept in two taps", body: "Booking confirmed. Goes straight into Apple, Google or Outlook calendars." },
          ].map((s, i) => (
            <div key={s.title} className="card-base p-6">
              <div className="w-10 h-10 rounded-xl bg-sage-100 text-sage-600 grid place-items-center mb-4">
                <s.icon size={20} />
              </div>
              <div className="text-ink-500 text-xs font-bold tracking-widest uppercase mb-1">Step {i + 1}</div>
              <h3 className="font-heading text-lg font-semibold mb-1">{s.title}</h3>
              <p className="text-ink-600 text-sm leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA strip */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
        <div className="rounded-3xl bg-sage-500 text-white p-8 sm:p-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div>
            <h3 className="font-heading text-2xl sm:text-3xl font-semibold mb-2">Ready to retire the WhatsApp group?</h3>
            <p className="text-sage-100/95 text-base">Free for the community. Made for all musicians and the people who run their ensembles.</p>
          </div>
          <Link to="/register" className="rounded-full bg-white text-sage-600 px-6 py-3 font-medium hover:bg-ivory-100 transition" data-testid="land-cta-bottom">
            Create your AMDS account
          </Link>
        </div>
      </section>

      <footer className="max-w-6xl mx-auto px-4 sm:px-6 py-10 text-sm text-ink-500 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-ivory-200">
        <div className="flex items-center gap-2">
          <Music2 size={16} />
          <span>AMDS — All Musicians Diary Service</span>
        </div>
        <div className="flex items-center gap-4">
          <span className="inline-flex items-center gap-1.5"><MapPin size={14} /> Community-built</span>
          <Link to="/login" className="hover:text-sage-600">Log in</Link>
          <Link to="/register" className="hover:text-sage-600">Sign up</Link>
        </div>
      </footer>
    </div>
  );
}
